import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Checkbox,
  Divider,
  Group,
  Menu,
  SegmentedControl,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core'
import { openConfirmModal } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import {
  IconChartBar,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconCopy,
  IconTrash,
  IconStack2,
  IconRefresh,
  IconTable,
} from '@tabler/icons-react'
import { ScheduleShiftsTodayCard } from '@/components/schedules/ScheduleShiftsTodayCard'
import { PageShell } from '@/components/ui'
import { useAuth } from '@/lib/auth-context'
import { useSearchParams } from 'react-router-dom'
import { api, ApiError } from '@/lib/api'
import {
  canAccessScheduleStaffingAnalysis,
  canAccessSchedulesNavigation,
  defaultPermissionsForRole,
  hasPermission,
  hasScheduleActualAccess,
  hasSchedulePlanAccess,
  loadMyPermissions,
  readCachedPermissions,
} from '@/lib/permissions'
import type {
  ColleaguesTodayResponse,
  MyPermissionsResponse,
  ScheduleResponseRow,
  ScheduleUserRow,
  ShiftLogMonthRow,
} from '@/types/api'
import { ScheduleEditModal } from '@/pages/schedules/ScheduleEditModal'
import { ScheduleStaffingAnalysisPanel } from '@/pages/schedules/ScheduleStaffingAnalysisPanel'
import { ScheduleTemplateModal } from '@/pages/schedules/ScheduleTemplateModal'
import {
  buildBreakSegmentsByUserAndDate,
  buildShiftSegmentsByUserAndDate,
  actualWorkedMinutesForDate,
  formatBreakLine,
  formatHoursFromMinutes,
  formatIntervalLine,
  formatShiftSegmentRange,
  getBreakIntervals,
  getWorkIntervals,
  intervalEndForPlanDay,
  padTimeStr,
  pad2,
  todayStrInMsk,
  type ScheduleEntry,
  utcSegmentToMskMinutesOnCalendarDate,
  userMonthWorkingHours,
} from '@/pages/schedules/schedule-utils'

type View = 'plan' | 'shifts' | 'diffs' | 'analysis'

/** Как дублировать на следующий месяц / шаблон: по номеру дня или по дню недели (как Python weekday: пн=0 … вс=6). */
type ScheduleDuplicateMode = 'by_day' | 'by_weekday'

/** День недели как в Python `date.weekday()` (понедельник 0 … воскресенье 6). */
function pythonWeekdayFromDateStr(dateStr: string): number {
  const parts = dateStr.split('-').map(Number)
  const y = parts[0]
  const mo = parts[1]
  const d = parts[2]
  const dt = new Date(y, mo - 1, d)
  return (dt.getDay() + 6) % 7
}

function ScheduleTransferModeConfirmBody({
  initialMode,
  onModeChange,
  intro,
}: {
  initialMode: ScheduleDuplicateMode
  onModeChange: (m: ScheduleDuplicateMode) => void
  intro: ReactNode
}) {
  const [mode, setMode] = useState<ScheduleDuplicateMode>(initialMode)
  return (
    <Stack gap="sm">
      {intro}
      <SegmentedControl
        value={mode}
        onChange={(v) => {
          const n = v as ScheduleDuplicateMode
          setMode(n)
          onModeChange(n)
        }}
        data={[
          { value: 'by_day', label: 'По числам' },
          { value: 'by_weekday', label: 'По дням недели' },
        ]}
      />
      <Text size="xs" c="dimmed">
        {mode === 'by_weekday'
          ? 'По дням недели: для каждого дня недели берётся график с первого такого дня в исходном месяце; он переносится на все соответствующие дни цели (пн–вс, как в календаре графика).'
          : 'По числам: 1-е число → 1-е число целевого месяца, если день есть (31→31 или пропуск в коротком месяце).'}
      </Text>
    </Stack>
  )
}

const MONTH_NAMES = [
  'Январь',
  'Февраль',
  'Март',
  'Апрель',
  'Май',
  'Июнь',
  'Июль',
  'Август',
  'Сентябрь',
  'Октябрь',
  'Ноябрь',
  'Декабрь',
]

const WEEK_SHORT = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб']

/** Высота первой строки заголовка (день недели) — должна совпадать с `top` второй строки (число). */
const SCHEDULE_HEAD_ROW1_PX = 36
/** Высота второй строки заголовка (число месяца). */
const SCHEDULE_HEAD_ROW2_PX = 40

const scheduleTableScrollBox: CSSProperties = {
  overflow: 'auto',
  width: '100%',
  maxHeight: 'min(72vh, calc(100vh - 220px))',
  minHeight: 240,
  WebkitOverflowScrolling: 'touch',
  overscrollBehavior: 'contain',
}

const scheduleStickyNameColBase: CSSProperties = {
  position: 'sticky',
  left: 0,
  minWidth: 160,
  maxWidth: 220,
  background: 'var(--mantine-color-body)',
  borderRight: '1px solid var(--mantine-color-default-border)',
  boxShadow: '4px 0 8px -4px rgba(0,0,0,0.12)',
}

function schedulesToByDate(rows: ScheduleResponseRow[]): Record<string, ScheduleResponseRow> {
  const m: Record<string, ScheduleResponseRow> = {}
  for (const s of rows) {
    m[String(s.date).slice(0, 10)] = s
  }
  return m
}

function diffStatus(
  dayPlanMin: number,
  dayFactMin: number,
  planIntervals: { start: number; end: number }[],
  factIntervals: { start: number; end: number }[],
): 'absent' | 'extra' | 'worked' | 'empty' {
  const DIFF_THRESHOLD = 5
  if (dayPlanMin === 0 && dayFactMin === 0) return 'empty'
  if (dayPlanMin > 0 && dayFactMin === 0) return 'absent'
  if (dayPlanMin === 0 && dayFactMin > 0) return 'extra'
  let extraMinutes = 0
  if (factIntervals.length > 0 && planIntervals.length > 0) {
    for (const fi of factIntervals) {
      let covered = 0
      for (const pi of planIntervals) {
        const overlapStart = Math.max(fi.start, pi.start)
        const overlapEnd = Math.min(fi.end, pi.end)
        if (overlapEnd > overlapStart) covered += overlapEnd - overlapStart
      }
      const total = fi.end - fi.start
      if (total > covered) extraMinutes += total - covered
    }
  } else if (factIntervals.length > 0 && planIntervals.length === 0) {
    extraMinutes = dayFactMin
  }
  if (extraMinutes > DIFF_THRESHOLD) return 'extra'
  return 'worked'
}

export function SchedulesPage() {
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const [perm, setPerm] = useState<MyPermissionsResponse | null>(() => readCachedPermissions())
  useEffect(() => {
    void loadMyPermissions(user?.role).then(setPerm)
  }, [user?.role])

  const [breakMinutesPerPlannedHour, setBreakMinutesPerPlannedHour] = useState(0)
  useEffect(() => {
    void api.workspaceMe()
      .then((ws) => setBreakMinutesPerPlannedHour(Number(ws.break_minutes_per_planned_hour) || 0))
      .catch(() => setBreakMinutesPerPlannedHour(0))
  }, [])

  const effectivePerm = perm ?? defaultPermissionsForRole(user?.role)
  const canNav = canAccessSchedulesNavigation(effectivePerm)
  const canPlan = hasSchedulePlanAccess(effectivePerm)
  const canActual = hasScheduleActualAccess(effectivePerm)
  const canDiff = canPlan && canActual
  const canAnalysis = canAccessScheduleStaffingAnalysis(effectivePerm)
  const canEditPlan = canPlan && hasPermission(effectivePerm, 'manage_projects')
  const canTemplate = canEditPlan

  const [year, setYear] = useState(() => new Date().getFullYear())
  const [month, setMonth] = useState(() => new Date().getMonth() + 1)
  const [analysisRefreshTick, setAnalysisRefreshTick] = useState(0)

  const view = useMemo((): View => {
    const tab = searchParams.get('tab')
    if (tab === 'analysis' && canAnalysis) return 'analysis'
    if (tab === 'shifts' && canActual) return 'shifts'
    if (tab === 'diffs' && canDiff) return 'diffs'
    if (tab === 'plan' && canPlan) return 'plan'
    if (canPlan) return 'plan'
    if (canActual) return 'shifts'
    return 'plan'
  }, [searchParams, canPlan, canActual, canDiff, canAnalysis])

  const setView = useCallback(
    (v: View) => {
      setSearchParams(
        (prev) => {
          const n = new URLSearchParams(prev)
          if (v === 'plan') n.delete('tab')
          else n.set('tab', v)
          return n
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  /** URL и фактический режим должны совпадать (недопустимый tab=… или отзыв прав). */
  useEffect(() => {
    const tab = searchParams.get('tab')
    const want = view === 'plan' ? null : view
    const cur = tab || null
    if (String(want ?? '') !== String(cur ?? '')) {
      setSearchParams(
        (prev) => {
          const n = new URLSearchParams(prev)
          if (view === 'plan') n.delete('tab')
          else n.set('tab', view)
          return n
        },
        { replace: true },
      )
    }
  }, [view, searchParams, setSearchParams])

  /** Последний выбранный режим переноса (числа / дни недели) в модалках: дублирование, шаблон. */
  const scheduleTransferModeRef = useRef<ScheduleDuplicateMode>('by_day')
  const [templateModalOpen, setTemplateModalOpen] = useState(false)

  const [users, setUsers] = useState<ScheduleUserRow[]>([])
  const [allSchedules, setAllSchedules] = useState<Record<string, Record<string, ScheduleResponseRow>>>({})
  const [shiftLogs, setShiftLogs] = useState<ShiftLogMonthRow[]>([])
  const [colleaguesToday, setColleaguesToday] = useState<ColleaguesTodayResponse | null>(null)
  const [colleaguesErr, setColleaguesErr] = useState<string | null>(null)
  const [colleaguesLoading, setColleaguesLoading] = useState(false)
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [loadingGrid, setLoadingGrid] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [hideAdmins, setHideAdmins] = useState(true)
  const [useLocalTz, setUseLocalTz] = useState(() => localStorage.getItem('scheduleUseLocalTz') === 'true')

  const [editModal, setEditModal] = useState<{ userId: string; dateStr: string } | null>(null)
  const [onShiftTick, setOnShiftTick] = useState(0)
  const [rangeStart, setRangeStart] = useState(() => `${new Date().getFullYear()}-${pad2(new Date().getMonth() + 1)}-01`)
  const [rangeEnd, setRangeEnd] = useState(() => todayStrInMsk())
  const [rangeSummary, setRangeSummary] = useState<{
    planned_minutes: number
    actual_minutes: number
    diff_minutes: number
    users_count: number
  } | null>(null)
  const [rangeLoading, setRangeLoading] = useState(false)
  const [bulkDeleting, setBulkDeleting] = useState(false)
  const [selectedDates, setSelectedDates] = useState<Set<string>>(new Set())

  const maxYear = canPlan ? 2030 : 2035
  const yearOptions = useMemo(() => {
    const y = new Date().getFullYear()
    const out: number[] = []
    for (let i = y - 2; i <= Math.min(y + 2, maxYear); i++) {
      if (i >= 2020) out.push(i)
    }
    return out.length ? out : [y]
  }, [maxYear])

  useEffect(() => {
    if (year > maxYear) setYear(maxYear)
  }, [maxYear, year])

  useEffect(() => {
    localStorage.setItem('scheduleUseLocalTz', useLocalTz ? 'true' : 'false')
  }, [useLocalTz])

  useEffect(() => {
    const lastDay = new Date(year, month, 0).getDate()
    setRangeStart(`${year}-${pad2(month)}-01`)
    setRangeEnd(`${year}-${pad2(month)}-${pad2(lastDay)}`)
    setRangeSummary(null)
    setSelectedDates(new Set())
  }, [year, month])

  useEffect(() => {
    const t = setInterval(() => setOnShiftTick((x) => x + 1), 60_000)
    return () => clearInterval(t)
  }, [])

  const loadUsers = useCallback(async () => {
    if (!canNav) {
      setLoadingUsers(false)
      return
    }
    setLoadingUsers(true)
    setLoadError(null)
    try {
      const list = await api.scheduleUsers()
      setUsers(list)
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Не удалось загрузить список сотрудников'
      setLoadError(msg)
      setUsers([])
    } finally {
      setLoadingUsers(false)
    }
  }, [canNav])

  useEffect(() => {
    void loadUsers()
  }, [loadUsers])

  const loadAllSchedules = useCallback(async () => {
    if (!canPlan || users.length === 0) return
    setLoadingGrid(true)
    setLoadError(null)
    try {
      const next: Record<string, Record<string, ScheduleResponseRow>> = {}
      await Promise.all(
        users.map(async (u) => {
          try {
            const res = await api.scheduleMonth(u.id, year, month)
            next[u.id] = schedulesToByDate(res.schedules)
          } catch {
            next[u.id] = {}
          }
        }),
      )
      setAllSchedules(next)
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Ошибка загрузки графика'
      setLoadError(msg)
    } finally {
      setLoadingGrid(false)
    }
  }, [canPlan, users, year, month])

  const loadShiftLogs = useCallback(async () => {
    if (!canActual) return
    setLoadingGrid(true)
    try {
      const res = await api.scheduleShiftsMonth(year, month)
      setShiftLogs(res.logs || [])
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Не удалось загрузить смены'
      showNotification({ title: 'Ошибка', message: msg, color: 'red' })
      setShiftLogs([])
    } finally {
      setLoadingGrid(false)
    }
  }, [canActual, year, month])

  useEffect(() => {
    if (canPlan) void loadAllSchedules()
  }, [canPlan, loadAllSchedules])

  const loadColleaguesToday = useCallback(async (silent?: boolean) => {
    if (!silent) setColleaguesLoading(true)
    try {
      const col = await api.scheduleColleaguesToday()
      setColleaguesToday(col)
      setColleaguesErr(null)
    } catch (e) {
      setColleaguesToday(null)
      setColleaguesErr(
        e instanceof ApiError ? e.message : 'Не удалось загрузить список коллег на смене',
      )
    } finally {
      if (!silent) setColleaguesLoading(false)
    }
  }, [])

  useEffect(() => {
    void loadColleaguesToday()
  }, [loadColleaguesToday])

  useEffect(() => {
    if (onShiftTick === 0) return
    void loadColleaguesToday(true)
  }, [onShiftTick, loadColleaguesToday])

  useEffect(() => {
    if (canActual && (view === 'shifts' || view === 'diffs')) void loadShiftLogs()
  }, [canActual, view, loadShiftLogs])

  useEffect(() => {
    if (view === 'diffs' && canPlan) void loadAllSchedules()
  }, [view, canPlan, loadAllSchedules])

  const filteredUsers = useMemo(() => {
    let list = users
    if (hideAdmins) list = list.filter((u) => u.role !== 'admin' && u.role !== 'owner')
    if (selectedIds.size > 0) list = list.filter((u) => selectedIds.has(u.id))
    return list
  }, [users, hideAdmins, selectedIds])

  const shiftSegments = useMemo(
    () =>
      buildShiftSegmentsByUserAndDate(
        shiftLogs.map((l) => ({
          user_id: l.user_id,
          started_at: l.started_at,
          ended_at: l.ended_at,
        })),
      ),
    [shiftLogs],
  )

  const breakSegments = useMemo(() => buildBreakSegmentsByUserAndDate(shiftLogs), [shiftLogs])

  const totalDays = useMemo(() => new Date(year, month, 0).getDate(), [year, month])

  const dayMeta = useMemo(() => {
    const rows: { day: number; dateStr: string; dow: number; isWeekend: boolean; isToday: boolean }[] = []
    const todayMsk = todayStrInMsk()
    for (let day = 1; day <= totalDays; day++) {
      const dateStr = `${year}-${pad2(month)}-${pad2(day)}`
      const d = new Date(year, month - 1, day)
      const dow = d.getDay()
      const isWeekend = dow === 0 || dow === 6
      const isToday = dateStr === todayMsk
      rows.push({ day, dateStr, dow, isWeekend, isToday })
    }
    return rows
  }, [year, month, totalDays, onShiftTick])

  const visibleUserIds = useMemo(() => filteredUsers.map((u) => u.id), [filteredUsers])

  const selectedDateList = useMemo(() => [...selectedDates].sort(), [selectedDates])

  const toggleSelectedDate = useCallback((dateStr: string) => {
    setSelectedDates((prev) => {
      const next = new Set(prev)
      if (next.has(dateStr)) next.delete(dateStr)
      else next.add(dateStr)
      return next
    })
  }, [])

  const selectRangeDates = useCallback(() => {
    if (!rangeStart || !rangeEnd || rangeEnd < rangeStart) return
    const next = new Set<string>()
    for (const m of dayMeta) {
      if (m.dateStr >= rangeStart && m.dateStr <= rangeEnd) next.add(m.dateStr)
    }
    setSelectedDates(next)
  }, [dayMeta, rangeEnd, rangeStart])

  const loadRangeSummary = async () => {
    if (!canDiff || !rangeStart || !rangeEnd) return
    if (rangeEnd < rangeStart) {
      showNotification({ title: 'Период', message: 'Дата окончания меньше даты начала', color: 'yellow' })
      return
    }
    setRangeLoading(true)
    try {
      const res = await api.scheduleRangeSummary({
        start_date: rangeStart,
        end_date: rangeEnd,
        user_ids: visibleUserIds.length ? visibleUserIds : undefined,
      })
      setRangeSummary({
        planned_minutes: Number(res.planned_minutes || 0),
        actual_minutes: Number(res.actual_minutes || 0),
        diff_minutes: Number(res.diff_minutes || 0),
        users_count: Number(res.users_count || 0),
      })
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось посчитать период',
        color: 'red',
      })
    } finally {
      setRangeLoading(false)
    }
  }

  const handleBulkDeleteRange = async () => {
    if (!canEditPlan || !rangeStart || !rangeEnd) return
    if (rangeEnd < rangeStart) {
      showNotification({ title: 'Период', message: 'Дата окончания меньше даты начала', color: 'yellow' })
      return
    }
    const targets = visibleUserIds
    if (targets.length === 0) {
      showNotification({ title: 'Удаление', message: 'Нет выбранных сотрудников', color: 'yellow' })
      return
    }
    const deleteDates = selectedDateList.length > 0 ? selectedDateList : []
    const periodText = deleteDates.length > 0
      ? `${deleteDates.length} выбранных дней`
      : `${rangeStart} — ${rangeEnd}`
    const ok = await new Promise<boolean>((resolve) => {
      openConfirmModal({
        title: 'Удалить рабочие дни',
        children: (
          <Text size="sm">
            Удалить плановые рабочие дни за период {periodText} для сотрудников: {targets.length}?
          </Text>
        ),
        labels: { confirm: 'Удалить', cancel: 'Отмена' },
        confirmProps: { color: 'red' },
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
      })
    })
    if (!ok) return
    setBulkDeleting(true)
    try {
      let deleted = 0
      if (deleteDates.length > 0) {
        for (const dateStr of deleteDates) {
          const res = await api.scheduleBulkDeleteRange({
            start_date: dateStr,
            end_date: dateStr,
            user_ids: targets,
          })
          deleted += Number(res.deleted || 0)
        }
      } else {
        const res = await api.scheduleBulkDeleteRange({
          start_date: rangeStart,
          end_date: rangeEnd,
          user_ids: targets,
        })
        deleted = Number(res.deleted || 0)
      }
      showNotification({
        title: 'Удалено',
        message: `Удалено записей: ${deleted}`,
        color: 'green',
      })
      setSelectedDates(new Set())
      await loadAllSchedules()
      if (view === 'diffs') void loadRangeSummary()
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось удалить рабочие дни',
        color: 'red',
      })
    } finally {
      setBulkDeleting(false)
    }
  }

  const openEdit = (userId: string, dateStr: string) => {
    if (!canEditPlan || view !== 'plan') return
    setEditModal({ userId, dateStr })
  }

  const handleRefresh = async () => {
    if (view === 'analysis') {
      setAnalysisRefreshTick((t) => t + 1)
      await loadColleaguesToday(true)
      showNotification({ title: 'Обновлено', message: 'Данные перезагружены', color: 'green' })
      return
    }
    if (view === 'plan' || view === 'diffs') {
      await loadAllSchedules()
    }
    if (view === 'shifts' || view === 'diffs') await loadShiftLogs()
    await loadColleaguesToday(true)
    showNotification({ title: 'Обновлено', message: 'Данные табеля перезагружены', color: 'green' })
  }

  const handleCopyNextMonth = async () => {
    if (!canEditPlan) return
    const manage = hasPermission(effectivePerm, 'manage_projects')
    const base = hideAdmins ? users.filter((u) => u.role !== 'admin' && u.role !== 'owner') : users
    let toCopy = manage ? base : base.filter((u) => u.id === user?.id)
    if (selectedIds.size > 0) toCopy = toCopy.filter((u) => selectedIds.has(u.id))
    if (toCopy.length === 0) {
      showNotification({ title: 'Нет данных', message: 'Нет пользователей для копирования', color: 'yellow' })
      return
    }

    let nextY = year
    let nextM = month + 1
    if (nextM > 12) {
      nextM = 1
      nextY += 1
    }
    if (nextY > 2030) {
      showNotification({ title: 'Год', message: 'Следующий месяц выходит за пределы доступного диапазона плана', color: 'yellow' })
      return
    }

    const ok = await new Promise<boolean>((resolve) => {
      openConfirmModal({
        title: 'Дублировать график',
        children: (
          <ScheduleTransferModeConfirmBody
            initialMode={scheduleTransferModeRef.current}
            onModeChange={(m) => {
              scheduleTransferModeRef.current = m
            }}
            intro={<Text size="sm">Дублировать график текущего месяца на следующий?</Text>}
          />
        ),
        labels: { confirm: 'Да', cancel: 'Отмена' },
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
      })
    })
    if (!ok) return

    const byWeekday = scheduleTransferModeRef.current === 'by_weekday'

    const nextSchedules: Record<string, Record<string, ScheduleResponseRow>> = {}
    for (const u of toCopy) {
      try {
        const res = await api.scheduleMonth(u.id, nextY, nextM)
        nextSchedules[u.id] = schedulesToByDate(res.schedules)
      } catch {
        nextSchedules[u.id] = {}
      }
    }
    let existingCount = 0
    for (const uid of Object.keys(nextSchedules)) {
      existingCount += Object.keys(nextSchedules[uid] || {}).length
    }
    if (existingCount > 0) {
      const ok2 = await new Promise<boolean>((resolve) => {
        openConfirmModal({
          title: 'Замена',
          children: (
            <Text size="sm">В следующем месяце уже есть записи. Заменить имеющееся заполненное время?</Text>
          ),
          labels: { confirm: 'Заменить', cancel: 'Отмена' },
          onConfirm: () => resolve(true),
          onCancel: () => resolve(false),
        })
      })
      if (!ok2) return
    }

    const nextLast = new Date(nextY, nextM, 0).getDate()
    let copied = 0
    let updated = 0
    try {
      for (const u of toCopy) {
        const userSched = allSchedules[u.id] || {}
        const nextByUser = nextSchedules[u.id] || {}

        const copyScheduleToDay = async (targetDateStr: string, schedule: ScheduleEntry) => {
          const wi = getWorkIntervals(schedule).map((i) => ({
            start_time: padTimeStr(i.start_time),
            end_time: padTimeStr(i.end_time),
          }))
          if (!wi.length) return
          const brRaw = getBreakIntervals(schedule)
          const br = brRaw.map((b) => ({
            start_time: padTimeStr(b.start_time),
            end_time: padTimeStr(b.end_time),
          }))
          const payloadUpdate = {
            work_intervals: wi,
            break_intervals: br.length && br[0].start_time && br[0].end_time ? [br[0]] : [],
          }
          const existing = nextByUser[targetDateStr]
          if (existing?.id != null) {
            await api.scheduleUpdate(Number(existing.id), payloadUpdate)
            updated++
          } else {
            await api.scheduleCreate({
              user_id: u.id,
              date: targetDateStr,
              work_intervals: payloadUpdate.work_intervals,
              break_start: br[0]?.start_time || undefined,
              break_end: br[0]?.end_time || undefined,
            })
            copied++
          }
        }

        if (!byWeekday) {
          for (const dateStr of Object.keys(userSched)) {
            const schedule = userSched[dateStr]
            if (!schedule) continue
            const dayNum = parseInt(dateStr.split('-')[2], 10)
            if (dayNum > nextLast) continue
            const targetDate = `${nextY}-${pad2(nextM)}-${pad2(dayNum)}`
            await copyScheduleToDay(targetDate, schedule as ScheduleEntry)
          }
        } else {
          const sortedDates = Object.keys(userSched).sort()
          const weekdayToSchedule: Record<number, ScheduleEntry> = {}
          for (const dateStr of sortedDates) {
            const schedule = userSched[dateStr]
            if (!schedule) continue
            const wi = getWorkIntervals(schedule as ScheduleEntry)
            if (!wi.length) continue
            const pyWd = pythonWeekdayFromDateStr(dateStr)
            if (weekdayToSchedule[pyWd] !== undefined) continue
            weekdayToSchedule[pyWd] = schedule as ScheduleEntry
          }
          for (let day = 1; day <= nextLast; day++) {
            const targetDateStr = `${nextY}-${pad2(nextM)}-${pad2(day)}`
            const pyWd = pythonWeekdayFromDateStr(targetDateStr)
            const schedule = weekdayToSchedule[pyWd]
            if (!schedule) continue
            await copyScheduleToDay(targetDateStr, schedule)
          }
        }
      }
      const total = copied + updated
      if (total > 0) {
        showNotification({
          title: 'Готово',
          message: `Скопировано: ${total} (${copied} создано, ${updated} обновлено)`,
          color: 'green',
        })
        await loadAllSchedules()
      } else {
        showNotification({ title: 'Нет записей', message: 'В текущем месяце нечего копировать', color: 'yellow' })
      }
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Ошибка копирования'
      showNotification({ title: 'Ошибка', message: msg, color: 'red' })
    }
  }

  const handleOpenTemplate = () => {
    setTemplateModalOpen(true)
  }

  const handleSaveTemplate = async () => {
    const ok = await new Promise<boolean>((resolve) => {
      openConfirmModal({
        title: 'Сделать шаблоном',
        children: (
          <ScheduleTransferModeConfirmBody
            initialMode={scheduleTransferModeRef.current}
            onModeChange={(m) => {
              scheduleTransferModeRef.current = m
            }}
            intro={
              <Text size="sm">
                Сохранить график текущего месяца как шаблон? Он будет использоваться при «Применить шаблон» и в окне «Шаблон».
              </Text>
            }
          />
        ),
        labels: { confirm: 'Сохранить', cancel: 'Отмена' },
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
      })
    })
    if (!ok) return
    const byWeekdaySave = scheduleTransferModeRef.current === 'by_weekday'
    try {
      await api.scheduleTemplateFromMonth(year, month, byWeekdaySave)
      showNotification({
        title: 'Шаблон',
        message: byWeekdaySave
          ? 'Текущий месяц сохранён как шаблон (перенос по дням недели)'
          : 'Текущий месяц сохранён как шаблон (перенос по числам месяца)',
        color: 'green',
      })
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Ошибка'
      showNotification({ title: 'Ошибка', message: msg, color: 'red' })
    }
  }

  const handleApplyTemplate = async () => {
    const ok = await new Promise<boolean>((resolve) => {
      openConfirmModal({
        title: 'Применить шаблон',
        children: (
          <ScheduleTransferModeConfirmBody
            initialMode={scheduleTransferModeRef.current}
            onModeChange={(m) => {
              scheduleTransferModeRef.current = m
            }}
            intro={
              <Text size="sm">
                Применить шаблон к {MONTH_NAMES[month - 1]} {year}? Существующий график за месяц будет очищен.
              </Text>
            }
          />
        ),
        labels: { confirm: 'Применить', cancel: 'Отмена' },
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
      })
    })
    if (!ok) return
    const byWeekday = scheduleTransferModeRef.current === 'by_weekday'
    try {
      await api.scheduleTemplateApply(year, month, true, byWeekday)
      showNotification({ title: 'Готово', message: 'Шаблон применён', color: 'green' })
      await loadAllSchedules()
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Ошибка'
      showNotification({ title: 'Ошибка', message: msg, color: 'red' })
    }
  }

  const stickyHeadStaff = useMemo(
    (): CSSProperties => ({
      ...scheduleStickyNameColBase,
      top: 0,
      zIndex: 6,
      verticalAlign: 'middle',
      textAlign: 'left',
      fontSize: 12,
    }),
    [],
  )

  const stickyHeadWeekday = (m: (typeof dayMeta)[0]): CSSProperties => ({
    position: 'sticky',
    top: 0,
    zIndex: 4,
    textAlign: 'center',
    fontSize: 11,
    minWidth: 56,
    height: SCHEDULE_HEAD_ROW1_PX,
    minHeight: SCHEDULE_HEAD_ROW1_PX,
    padding: '4px 2px',
    background: selectedDates.has(m.dateStr)
      ? 'var(--mantine-color-cyan-light)'
      : m.isWeekend
        ? 'var(--mantine-color-gray-2)'
        : 'var(--mantine-color-body)',
    boxShadow: 'inset 0 -1px 0 var(--mantine-color-default-border)',
    cursor: canEditPlan ? 'pointer' : 'default',
  })

  const stickyHeadDayNum = (m: (typeof dayMeta)[0]): CSSProperties => ({
    position: 'sticky',
    top: SCHEDULE_HEAD_ROW1_PX,
    zIndex: 4,
    textAlign: 'center',
    fontSize: 12,
    fontWeight: 600,
    minWidth: 56,
    height: SCHEDULE_HEAD_ROW2_PX,
    minHeight: SCHEDULE_HEAD_ROW2_PX,
    padding: '4px 2px',
    background: selectedDates.has(m.dateStr)
      ? 'var(--mantine-color-cyan-light)'
      : m.isToday
      ? 'var(--mantine-color-blue-light)'
      : m.isWeekend
        ? 'var(--mantine-color-gray-2)'
        : 'var(--mantine-color-body)',
    cursor: canEditPlan ? 'pointer' : 'default',
  })

  const stickyHeadHours = useMemo(
    (): CSSProperties => ({
      position: 'sticky',
      top: 0,
      zIndex: 5,
      verticalAlign: 'middle',
      textAlign: 'center',
      minWidth: view === 'diffs' ? 112 : 80,
      width: view === 'diffs' ? 112 : 80,
      background: 'var(--mantine-color-body)',
      fontSize: 12,
      boxShadow: 'inset 0 -1px 0 var(--mantine-color-default-border)',
    }),
    [view],
  )

  /** Совпадает с Mantine `Table striped`: нечётные tr (1-я, 3-я…) — полоса `--table-striped-color`. */
  const stickyBodyName = (idx: number): CSSProperties => ({
    ...scheduleStickyNameColBase,
    zIndex: 3,
    verticalAlign: 'top',
    background: idx % 2 === 0 ? 'var(--table-striped-color)' : 'var(--mantine-color-body)',
  })

  const renderPlanCell = (u: ScheduleUserRow, dateStr: string, meta: (typeof dayMeta)[0]) => {
    const schedule = allSchedules[u.id]?.[dateStr] as ScheduleEntry | undefined
    const line = formatIntervalLine(schedule, dateStr, useLocalTz)
    const br = formatBreakLine(schedule, dateStr, useLocalTz)
    const has = !!line
    const background = meta.isToday
      ? 'var(--mantine-color-blue-light)'
      : selectedDates.has(dateStr)
        ? 'var(--mantine-color-cyan-light)'
        : meta.isWeekend
        ? 'var(--mantine-color-gray-1)'
        : undefined
    return (
      <Table.Td
        key={dateStr}
        style={{
          background,
          fontSize: 11,
          verticalAlign: 'top',
          minWidth: 72,
          maxWidth: 120,
          borderLeft: selectedDates.has(dateStr) ? '2px solid var(--mantine-color-cyan-5)' : undefined,
          borderRight: selectedDates.has(dateStr) ? '2px solid var(--mantine-color-cyan-5)' : undefined,
          cursor: canEditPlan ? 'pointer' : 'default',
        }}
        onClick={() => openEdit(u.id, dateStr)}
      >
        {has ? (
          <Stack gap={2}>
            <Text size="xs" lh={1.3}>
              {line}
            </Text>
            {br ? (
              <Text size="xs" c="dimmed" lh={1.2}>
                п: {br}
              </Text>
            ) : null}
          </Stack>
        ) : (
          <Text size="xs" c="dimmed">
            —
          </Text>
        )}
      </Table.Td>
    )
  }

  const renderShiftCell = (u: ScheduleUserRow, dateStr: string, meta: (typeof dayMeta)[0]) => {
    const segments = shiftSegments[u.id]?.[dateStr] || []
    const breakSegs = breakSegments[u.id]?.[dateStr] || []
    const background = meta.isToday
      ? 'var(--mantine-color-blue-light)'
      : selectedDates.has(dateStr)
        ? 'var(--mantine-color-cyan-light)'
        : meta.isWeekend
        ? 'var(--mantine-color-gray-1)'
        : undefined
    const parts = segments.map((seg, idx) => {
      const line = formatShiftSegmentRange(seg, useLocalTz)
      return (
        <Text key={idx} size="xs" lh={1.3}>
          {line}
        </Text>
      )
    })
    const breakParts = breakSegs.map((b, idx) => {
      const line = formatShiftSegmentRange(b, useLocalTz)
      return (
        <Text key={`b-${idx}`} size="xs" c="orange" lh={1.2}>
          перерыв {line}
        </Text>
      )
    })
    const has = segments.length > 0
    const actualMin = actualWorkedMinutesForDate(segments, breakSegs, breakMinutesPerPlannedHour)
    return (
      <Table.Td
        key={dateStr}
        style={{
          background,
          fontSize: 11,
          verticalAlign: 'top',
          minWidth: 72,
          maxWidth: 120,
          borderLeft: selectedDates.has(dateStr) ? '2px solid var(--mantine-color-cyan-5)' : undefined,
          borderRight: selectedDates.has(dateStr) ? '2px solid var(--mantine-color-cyan-5)' : undefined,
        }}
      >
        {has ? (
          <Stack gap={2}>
            {parts}
            {breakParts}
            <Text size="xs" fw={600} lh={1.2}>
              итого {formatHoursFromMinutes(actualMin)}
            </Text>
          </Stack>
        ) : (
          <Text size="xs" c="dimmed">
            —
          </Text>
        )}
      </Table.Td>
    )
  }

  const renderDiffCell = (u: ScheduleUserRow, dateStr: string, meta: (typeof dayMeta)[0]) => {
    const schedule = allSchedules[u.id]?.[dateStr] as ScheduleEntry | undefined
    const segments = shiftSegments[u.id]?.[dateStr] || []
    const breakSegs = breakSegments[u.id]?.[dateStr] || []
    const planIntervals: { start: number; end: number; startStr: string; endStr: string }[] = []
    for (const i of getWorkIntervals(schedule)) {
      const [sh, sm] = i.start_time.split(':').map(Number)
      const [eh, em] = i.end_time.split(':').map(Number)
      if (Number.isNaN(sh) || Number.isNaN(eh)) continue
      const startMin = sh * 60 + (Number.isNaN(sm) ? 0 : sm)
      const rawEndMin = eh * 60 + (Number.isNaN(em) ? 0 : em)
      const endMin = intervalEndForPlanDay(startMin, rawEndMin)
      planIntervals.push({
        start: startMin,
        end: endMin,
        startStr: `${pad2(sh)}:${pad2(sm)}`,
        endStr: `${pad2(eh)}:${pad2(em)}`,
      })
    }
    const factIntervals: { start: number; end: number; startStr: string; endStr: string }[] = []
    for (const seg of segments) {
      const clipped = utcSegmentToMskMinutesOnCalendarDate(dateStr, seg)
      if (!clipped || clipped.end <= clipped.start) continue
      factIntervals.push({
        start: clipped.start,
        end: clipped.end,
        startStr: '',
        endStr: '',
      })
    }
    const dayPlanMin = planIntervals.reduce((s, i) => s + (i.end - i.start), 0)
    const dayFactMin = actualWorkedMinutesForDate(segments, breakSegs, breakMinutesPerPlannedHour)
    const st = diffStatus(dayPlanMin, dayFactMin, planIntervals, factIntervals)
    const bgBase = selectedDates.has(dateStr)
      ? 'var(--mantine-color-cyan-light)'
      : meta.isWeekend ? 'var(--mantine-color-gray-1)' : undefined
    let accent = bgBase
    if (st === 'absent') accent = 'var(--mantine-color-red-light)'
    else if (st === 'extra') accent = 'var(--mantine-color-yellow-light)'
    else if (st === 'worked' && (dayPlanMin > 0 || dayFactMin > 0)) accent = 'var(--mantine-color-green-light)'
    if (meta.isToday) accent = 'var(--mantine-color-blue-light)'

    const planStr = schedule ? formatIntervalLine(schedule, dateStr, useLocalTz) || '—' : '—'
    const factStr = segments.length ? segments.map((seg) => formatShiftSegmentRange(seg, useLocalTz)).join(', ') : '—'
    const diffMin = dayFactMin - dayPlanMin
    let diffText = ''
    if (Math.abs(diffMin) > 5) {
      const sign = diffMin > 0 ? '+' : '−'
      diffText = `${sign}${formatHoursFromMinutes(Math.abs(diffMin))}`
    }

    if (st === 'empty') {
      return (
        <Table.Td
          key={dateStr}
          style={{
            background: accent,
            minWidth: 88,
            maxWidth: 140,
            fontSize: 10,
            borderLeft: selectedDates.has(dateStr) ? '2px solid var(--mantine-color-cyan-5)' : undefined,
            borderRight: selectedDates.has(dateStr) ? '2px solid var(--mantine-color-cyan-5)' : undefined,
          }}
        >
          <Text size="xs" c="dimmed">
            —
          </Text>
        </Table.Td>
      )
    }

    return (
      <Table.Td
        key={dateStr}
        style={{
          background: accent,
          minWidth: 88,
          maxWidth: 140,
          fontSize: 10,
          verticalAlign: 'top',
          borderLeft: selectedDates.has(dateStr) ? '2px solid var(--mantine-color-cyan-5)' : undefined,
          borderRight: selectedDates.has(dateStr) ? '2px solid var(--mantine-color-cyan-5)' : undefined,
        }}
      >
        <Stack gap={2}>
          <Text size="xs" lh={1.25}>
            П: {planStr}
          </Text>
          <Text size="xs" lh={1.25}>
            Ф: {factStr}
          </Text>
          <Group gap={6} wrap="wrap">
            <Text size="xs" span>
              П: {formatHoursFromMinutes(dayPlanMin)}
            </Text>
            <Text size="xs" span>
              Ф: {formatHoursFromMinutes(dayFactMin)}
            </Text>
            {diffText ? (
              <Text size="xs" fw={600} span>
                {diffText}
              </Text>
            ) : null}
          </Group>
        </Stack>
      </Table.Td>
    )
  }

  const editExisting = editModal
    ? allSchedules[editModal.userId]?.[editModal.dateStr]
    : undefined

  const shiftsTodayCard = (
    <ScheduleShiftsTodayCard
      hideShiftActions
      colleaguesLoading={colleaguesLoading}
      colleaguesErr={colleaguesErr}
      colleaguesToday={colleaguesToday}
      onShiftMutated={() => void loadColleaguesToday(true)}
    />
  )

  return (
    <PageShell>
      <Stack gap="md">
        {loadError && (
          <Alert color="red" title="Ошибка" onClose={() => setLoadError(null)} withCloseButton>
            {loadError}
          </Alert>
        )}

        {canNav ? (
          <>
        <Card withBorder padding="md">
          <Stack gap="sm">
            <Group gap="xs" wrap="wrap" align="center">
                <Checkbox
                  label="Скрыть администраторов"
                  checked={hideAdmins}
                  onChange={(e) => setHideAdmins(e.currentTarget.checked)}
                />
                <Checkbox
                  label="По моему времени"
                  checked={useLocalTz}
                  onChange={(e) => setUseLocalTz(e.currentTarget.checked)}
                />
            </Group>
            <Divider />
            {shiftsTodayCard}
          </Stack>
        </Card>

        <Card withBorder padding={0}>
          <Group justify="space-between" p="md" wrap="wrap" align="center">
            <Group gap="xs" wrap="wrap">
              <IconTable size={18} />
              <Text fw={600}>Таблица</Text>
              {view !== 'analysis' && (
                <>
                  <NativeMonthYear
                    month={month}
                    year={year}
                    yearOptions={yearOptions}
                    onMonth={setMonth}
                    onYear={setYear}
                  />
                  <Button.Group>
                    <Button
                      variant="default"
                      size="sm"
                      px="xs"
                      onClick={() => {
                        if (month <= 1) {
                          setMonth(12)
                          setYear((y) => Math.max(2020, y - 1))
                        } else setMonth((m) => m - 1)
                      }}
                    >
                      <IconChevronLeft size={16} />
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      px="xs"
                      onClick={() => {
                        if (month >= 12) {
                          setMonth(1)
                          setYear((y) => Math.min(maxYear, y + 1))
                        } else setMonth((m) => m + 1)
                      }}
                    >
                      <IconChevronRight size={16} />
                    </Button>
                  </Button.Group>
                </>
              )}
              {canPlan && (
                <Button
                  size="xs"
                  variant={view === 'plan' ? 'filled' : 'light'}
                  onClick={() => setView('plan')}
                >
                  Плановый график
                </Button>
              )}
              {canActual && (
                <Button
                  size="xs"
                  variant={view === 'shifts' ? 'filled' : 'light'}
                  onClick={() => setView('shifts')}
                >
                  Фактические смены
                </Button>
              )}
              {canDiff && (
                <Button
                  size="xs"
                  variant={view === 'diffs' ? 'filled' : 'light'}
                  onClick={() => setView('diffs')}
                >
                  Расхождения
                </Button>
              )}
              {canAnalysis && (
                <Button
                  size="xs"
                  leftSection={<IconChartBar size={14} />}
                  variant={view === 'analysis' ? 'filled' : 'light'}
                  onClick={() => setView('analysis')}
                >
                  Анализ (AI)
                </Button>
              )}
            </Group>
            <Group gap="xs" wrap="wrap">
              <Button
                leftSection={<IconRefresh size={16} />}
                variant="default"
                size="sm"
                loading={view === 'analysis' ? false : loadingGrid}
                onClick={() => void handleRefresh()}
              >
                Обновить
              </Button>
              {canTemplate && view === 'plan' && (
                <>
                  <Button
                    leftSection={<IconCopy size={16} />}
                    variant="light"
                    size="sm"
                    onClick={() => void handleCopyNextMonth()}
                  >
                    Дублировать на следующий
                  </Button>
                  <Button
                    leftSection={<IconStack2 size={16} />}
                    variant="default"
                    size="sm"
                    onClick={() => void handleOpenTemplate()}
                  >
                    Шаблон
                  </Button>
                  <Button variant="default" size="sm" onClick={() => void handleSaveTemplate()}>
                    Сделать шаблоном
                  </Button>
                  <Button variant="filled" size="sm" onClick={() => void handleApplyTemplate()}>
                    Применить шаблон
                  </Button>
                </>
              )}
            </Group>
          </Group>

          {view === 'analysis' ? (
            <ScheduleStaffingAnalysisPanel refreshTick={analysisRefreshTick} />
          ) : (
            <>
          <Box
            mx="md"
            mb="sm"
            p="sm"
            style={{
              border: '1px solid var(--mantine-color-default-border)',
              borderRadius: 8,
              background: 'var(--mantine-color-gray-0)',
            }}
          >
            <Stack gap="sm">
              <Group justify="space-between" align="flex-start" wrap="wrap" gap="md">
                <Stack gap={4}>
                  <Text size="sm" fw={600}>
                    Выборка и массовые действия
                  </Text>
                  <Group gap={6} wrap="wrap">
                    <Badge variant="light" color="blue">
                      Операторов: {filteredUsers.length}
                    </Badge>
                    {selectedDates.size > 0 ? (
                      <Badge variant="light" color="cyan">
                        Выделено дней: {selectedDates.size}
                      </Badge>
                    ) : (
                      <Badge variant="light" color="gray">
                        Дни не выделены
                      </Badge>
                    )}
                    {view === 'diffs' ? (
                      <>
                        <Badge variant="light" color="green">в плане</Badge>
                        <Badge variant="light" color="red">не вышел</Badge>
                        <Badge variant="light" color="yellow">вне плана</Badge>
                      </>
                    ) : null}
                  </Group>
                </Stack>
                <Group gap="xs" wrap="wrap">
                  {selectedDates.size > 0 ? (
                    <Button size="compact-sm" variant="default" onClick={() => setSelectedDates(new Set())}>
                      Снять выделение
                    </Button>
                  ) : null}
                  <Button size="compact-sm" variant="default" onClick={selectRangeDates}>
                    Выделить период
                  </Button>
                </Group>
              </Group>

              <Group gap="xs" wrap="wrap" align="flex-end">
                <Menu shadow="md" width={260}>
                  <Menu.Target>
                    <Button variant="default" size="sm" rightSection={<IconChevronDown size={14} />}>
                      Операторы
                      {selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    <Menu.Label>Кого показывать</Menu.Label>
                    <Menu.Item
                      onClick={() => {
                        setSelectedIds(new Set())
                      }}
                    >
                      Все
                    </Menu.Item>
                    <Menu.Item
                      onClick={() => {
                        setSelectedIds(new Set(users.filter((u) => !hideAdmins || (u.role !== 'admin' && u.role !== 'owner')).map((u) => u.id)))
                      }}
                    >
                      Отметить видимых
                    </Menu.Item>
                    <Menu.Item onClick={() => setSelectedIds(new Set())}>Снять выбор</Menu.Item>
                    <Stack gap={4} p="xs" style={{ maxHeight: 280, overflow: 'auto' }}>
                      {users.map((u) => (
                        <Checkbox
                          key={u.id}
                          label={u.name}
                          checked={selectedIds.has(u.id)}
                          onChange={(e) => {
                            setSelectedIds((prev) => {
                              const n = new Set(prev)
                              if (e.currentTarget.checked) n.add(u.id)
                              else n.delete(u.id)
                              return n
                            })
                          }}
                        />
                      ))}
                    </Stack>
                  </Menu.Dropdown>
                </Menu>
                {canDiff ? (
                  <>
                    <TextInput
                      size="sm"
                      type="date"
                      label="Период с"
                      value={rangeStart}
                      onChange={(e) => setRangeStart(e.currentTarget.value)}
                    />
                    <TextInput
                      size="sm"
                      type="date"
                      label="по"
                      value={rangeEnd}
                      onChange={(e) => setRangeEnd(e.currentTarget.value)}
                    />
                    <Button size="sm" variant="light" loading={rangeLoading} onClick={() => void loadRangeSummary()}>
                      Посчитать
                    </Button>
                  </>
                ) : null}
                {canEditPlan ? (
                  <Button
                    size="sm"
                    variant="default"
                    color="red"
                    leftSection={<IconTrash size={15} />}
                    loading={bulkDeleting}
                    disabled={filteredUsers.length === 0}
                    onClick={() => void handleBulkDeleteRange()}
                  >
                    {selectedDates.size > 0 ? 'Удалить выделенные' : 'Удалить период'}
                  </Button>
                ) : null}
              </Group>
              {rangeSummary ? (
                <Group gap="xs" wrap="wrap">
                  <Badge size="lg" variant="light" color="gray">Сотрудников: {rangeSummary.users_count}</Badge>
                  <Badge size="lg" variant="light" color="blue">План: {formatHoursFromMinutes(rangeSummary.planned_minutes)}</Badge>
                  <Badge size="lg" variant="light" color="teal">Факт: {formatHoursFromMinutes(rangeSummary.actual_minutes)}</Badge>
                  <Badge size="lg" variant="light" color={rangeSummary.diff_minutes < 0 ? 'red' : 'green'}>
                    Расхождение: {rangeSummary.diff_minutes >= 0 ? '+' : '−'}
                    {formatHoursFromMinutes(Math.abs(rangeSummary.diff_minutes))}
                  </Badge>
                </Group>
              ) : null}
              <Text size="xs" c="dimmed">
                Для удаления отдельных отпускных дней нажмите на числа в заголовке таблицы: выбранные колонки подсветятся.
              </Text>
            </Stack>
          </Box>

          <Box style={scheduleTableScrollBox}>
            <Table
              striped
              highlightOnHover
              withTableBorder
              style={{
                minWidth: 720,
                borderCollapse: 'separate',
                borderSpacing: 0,
              }}
            >
              <Table.Thead>
                <Table.Tr>
                  <Table.Th rowSpan={2} style={stickyHeadStaff}>
                    Сотрудник
                  </Table.Th>
                  {dayMeta.map((m) => (
                    <Table.Th
                      key={m.dateStr}
                      style={stickyHeadWeekday(m)}
                      title={canEditPlan ? 'Выделить день для массового действия' : undefined}
                      onClick={() => {
                        if (canEditPlan) toggleSelectedDate(m.dateStr)
                      }}
                    >
                      <div>{WEEK_SHORT[m.dow]}</div>
                    </Table.Th>
                  ))}
                  <Table.Th rowSpan={2} style={stickyHeadHours}>
                    {view === 'diffs' ? 'План / факт' : 'Часы'}
                  </Table.Th>
                </Table.Tr>
                <Table.Tr>
                  {dayMeta.map((m) => (
                    <Table.Th
                      key={`d-${m.dateStr}`}
                      style={stickyHeadDayNum(m)}
                      title={canEditPlan ? 'Выделить день для массового действия' : undefined}
                      onClick={() => {
                        if (canEditPlan) toggleSelectedDate(m.dateStr)
                      }}
                    >
                      <Stack gap={0} align="center">
                        <Text size="xs" fw={700} lh={1}>
                          {m.day}
                        </Text>
                        {selectedDates.has(m.dateStr) ? (
                          <Text size="xs" c="cyan" lh={1}>
                            выбран
                          </Text>
                        ) : null}
                      </Stack>
                    </Table.Th>
                  ))}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {loadingUsers ? (
                  <Table.Tr>
                    <Table.Td colSpan={totalDays + 2}>
                      <Text size="sm" c="dimmed">
                        Загрузка…
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : filteredUsers.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={totalDays + 2}>
                      <Text size="sm" c="dimmed">
                        Нет строк по фильтру
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  filteredUsers.map((u, idx) => {
                    const totalPlanH =
                      view === 'plan' || view === 'diffs'
                        ? userMonthWorkingHours(u.id, year, month, allSchedules as Record<string, Record<string, ScheduleEntry>>)
                        : 0
                    let totalFactMin = 0
                    if (view === 'shifts' || view === 'diffs') {
                      for (const m of dayMeta) {
                        const segs = shiftSegments[u.id]?.[m.dateStr] || []
                        const br = breakSegments[u.id]?.[m.dateStr] || []
                        totalFactMin += actualWorkedMinutesForDate(segs, br, breakMinutesPerPlannedHour)
                      }
                    }
                    return (
                      <Table.Tr key={u.id}>
                        <Table.Td style={stickyBodyName(idx)}>
                          <Text size="sm" fw={500} lineClamp={2}>
                            {u.name}
                          </Text>
                        </Table.Td>
                        {dayMeta.map((meta) => {
                          if (view === 'plan') return renderPlanCell(u, meta.dateStr, meta)
                          if (view === 'shifts') return renderShiftCell(u, meta.dateStr, meta)
                          return renderDiffCell(u, meta.dateStr, meta)
                        })}
                        <Table.Td
                          style={{
                            fontSize: 12,
                            verticalAlign: 'middle',
                            minWidth: view === 'diffs' ? 112 : 80,
                          }}
                        >
                          {view === 'plan' && (
                            <Text size="sm">
                              {totalPlanH > 0 ? formatHoursFromMinutes(Math.round(totalPlanH * 60)) : '—'}
                            </Text>
                          )}
                          {view === 'shifts' && (
                            <Text size="sm">
                              {totalFactMin > 0 ? formatHoursFromMinutes(totalFactMin) : '—'}
                            </Text>
                          )}
                          {view === 'diffs' && (
                            <Stack gap={2}>
                              <Text size="xs">
                                П:{' '}
                                {formatHoursFromMinutes(Math.round(totalPlanH * 60))}
                              </Text>
                              <Text size="xs">
                                Ф:{' '}
                                {formatHoursFromMinutes(totalFactMin)}
                              </Text>
                              <Text size="xs" fw={600}>
                                Δ: {totalFactMin - Math.round(totalPlanH * 60) >= 0 ? '+' : '−'}
                                {formatHoursFromMinutes(Math.abs(totalFactMin - Math.round(totalPlanH * 60)))}
                              </Text>
                            </Stack>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    )
                  })
                )}
              </Table.Tbody>
            </Table>
          </Box>
            </>
          )}
        </Card>
          </>
        ) : (
          <Card withBorder padding="md">{shiftsTodayCard}</Card>
        )}
      </Stack>

      {editModal && (
        <ScheduleEditModal
          opened
          onClose={() => setEditModal(null)}
          userId={editModal.userId}
          userName={users.find((x) => x.id === editModal.userId)?.name || ''}
          dateStr={editModal.dateStr}
          year={year}
          month={month}
          existing={editExisting}
          onReload={loadAllSchedules}
        />
      )}

      <ScheduleTemplateModal
        opened={templateModalOpen}
        onClose={() => setTemplateModalOpen(false)}
        canEdit={canEditPlan}
        onSaved={async () => {
          await loadAllSchedules()
        }}
      />
    </PageShell>
  )
}

function NativeMonthYear({
  month,
  year,
  yearOptions,
  onMonth,
  onYear,
}: {
  month: number
  year: number
  yearOptions: number[]
  onMonth: (m: number) => void
  onYear: (y: number) => void
}) {
  return (
    <Group gap="xs" wrap="nowrap">
      <select
        value={month}
        onChange={(e) => onMonth(Number(e.target.value))}
        style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--mantine-color-default-border)' }}
      >
        {MONTH_NAMES.map((name, i) => (
          <option key={name} value={i + 1}>
            {name}
          </option>
        ))}
      </select>
      <select
        value={year}
        onChange={(e) => onYear(Number(e.target.value))}
        style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--mantine-color-default-border)' }}
      >
        {yearOptions.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
    </Group>
  )
}
